import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Map, {
    FullscreenControl,
    Layer,
    Marker,
    NavigationControl,
    Source,
    useControl,
} from "react-map-gl";

import { MAPBOX_TOKEN, TILER_URL, API_URL } from "settings";

import DataControl from "./controls/data";
import DrawControl from "./controls/draw";
import HelpControl from "./controls/help";
import InspectControl from "./controls/inspect";
import LayerControl from "./controls/layer";
import ListsControl from "./controls/lists";
import LogoControl from "./controls/logo";
import MapContext from "./context";
import ParcelDetail from "./controls/parcel_detail";
import ToolsControl from "./controls/tools";
import { FilterControl, FilterPanel } from "./controls/filter";
import { SearchControl, SearchBox } from "./controls/search";

// Mapbox filters
const COUNTY_FILTER = [
    "all",
    ["==", ["get", "level"], 2],
    ["==", ["get", "iso_a2"], "US"],
];
const ZIP_FILTER = [
    "all",
    ["==", ["get", "level"], 1],
    ["==", ["get", "iso_a2"], "US"],
];

const initialViewState = {
    longitude: -80.49238,
    latitude: 34.30861,
    zoom: 14,
};

const maptilerSource = {
    id: "maptiler_source",
    type: "vector",
    tiles: [`${API_URL}/tiles/countries/{z}/{x}/{y}.pbf`],
};

const parcelSource = {
    id: "tiler_source",
    type: "vector",
    tiles: [`${TILER_URL}/maps/parcels/{z}/{x}/{y}.pbf`],
    promoteId: "id",
};

// Identify county layer
const countyIdentify = {
    id: "county_identify",
    type: "fill",
    "source-layer": "administrative",
    paint: { "fill-color": "#000000", "fill-opacity": 0 },
    filter: COUNTY_FILTER,
};

const countyOutline = {
    id: "county_outline",
    type: "line",
    "source-layer": "administrative",
    paint: {
        "line-width": 3,
        "line-color": "#ff00f2",
    },
    filter: COUNTY_FILTER,
};

// Highlight county shadow
const countyShadow = {
    id: "county_shadow",
    type: "fill",
    "source-layer": "administrative",
    paint: { "fill-color": "#000000", "fill-opacity": 0.5 },
};

// Identify zip layer
const zipIdentify = {
    id: "zip_identify",
    type: "fill",
    "source-layer": "postal",
    paint: { "fill-color": "#000000", "fill-opacity": 0 },
    filter: ZIP_FILTER,
};

const zipOutline = {
    id: "zip_outline",
    type: "line",
    "source-layer": "postal",
    paint: {
        "line-width": 3,
        "line-color": "#ff00f2",
    },
    filter: ZIP_FILTER,
};

// Highlight zip shadow
const zipShadow = {
    id: "zip_shadow",
    type: "fill",
    "source-layer": "postal",
    paint: { "fill-color": "#000000", "fill-opacity": 0.5 },
};

// Identify parcel layer
const parcelIdentify = {
    id: "parcel_identify",
    type: "fill",
    "source-layer": "parcels",
    paint: { "fill-color": "#000000", "fill-opacity": 0 },
};

// Highlight parcel shadow
const parcelShadow = {
    id: "parcel_shadow",
    type: "fill",
    "source-layer": "parcels",
    paint: { "fill-color": "#000000", "fill-opacity": 0.5 },
};

// Parcel outline layer
const parcelOutline = {
    id: "parcel_line",
    type: "line",
    "source-layer": "parcels",
    paint: {
        "line-width": [
            "case",
            ["boolean", ["feature-state", "searchResultMatch"], false],
            5, // True
            1, // False
        ],
        "line-color": [
            "case",
            ["boolean", ["feature-state", "searchResultMatch"], false],
            "#ff00f2", // True
            "#ffa500", // False
        ],
    },
};

// Active parcel layer
const parcelOutlineActive = {
    id: "parcel_line_active",
    type: "line",
    "source-layer": "parcels",
    paint: {
        "line-width": 4,
        "line-color": "#17c1e8",
    },
};

// Search result points
const searchResultPoints = {
    id: "search_result_points",
    type: "circle",
    maxzoom: 14,
    paint: {
        "circle-color": "#ff00f2",
        "circle-stroke-color": "black",
        "circle-stroke-width": 1,
        "circle-radius": 8,
        "circle-opacity": 1,
    },
};

export default function ParcelViewer() {
    const mapRef = useRef();
    const location = useLocation();

    const [activeParcelID, setActiveParcelID] = useState();

    const [showFilterPanel, setShowFilterPanel] = useState(true);
    const [showSearchBox, setShowSearchBox] = useState(true);
    const [searchResult, _setSearchResult] = useState();

    const setSearchResult = useCallback(
        (searchResult) => {
            _setSearchResult(searchResult);

            const parcels = searchResult?.parcels || [];

            // Fit map bounds to search results extent
            const bbox = calculateBounds(parcels);
            if (bbox) {
                mapRef.current.fitBounds(bbox, {
                    padding: 100,
                });
            }

            // Reset feature state of parcels layer
            mapRef.current.removeFeatureState({
                source: "tiler_source",
                sourceLayer: "parcels",
            });

            // Set feature state of each parcel in parcels layer if the ID exists in parcels searchResults
            parcels.forEach((parcel) => {
                mapRef.current.setFeatureState(
                    { source: "tiler_source", sourceLayer: "parcels", id: parcel.id },
                    { searchResultMatch: true },
                );
            });
        },
        [mapRef, _setSearchResult],
    );

    // Render no features
    const emptyFilter = ["==", "code", ""];

    const [mapFilter, setMapFilter] = useState();

    const resetViewState = useCallback(() => {
        const { longitude, latitude, zoom } = initialViewState;
        const center = [longitude, latitude];
        mapRef.current.easeTo({ center, zoom, bearing: 0, pitch: 0, duration: 0 });
    }, [mapRef]);

    const onClick = (e) => {
        const feature = e.features && e.features[0];
        const id = feature?.id;
        console.log("Clicked feature", feature);

        // Toggle active feature
        setActiveParcelID((oldValue) => (id === oldValue ? null : id));

        // Clear map filter if active parcel changed
        if (id !== activeParcelID) {
            setMapFilter();
        }
    };

    const activeParcelFilter = useMemo(
        () => ["==", "id", activeParcelID || ""],
        [activeParcelID],
    );

    // Set body class name on mount
    useEffect(() => {
        document.body.className = "parcel-viewer";
        return () => {
            // Clear class name on dismount
            document.body.className = "";
        };
    }, []);

    const context = { setMapFilter };

    // Apply map filter
    let zipFilter = emptyFilter;
    let countyFilter = emptyFilter;
    let parcelFilter = emptyFilter;
    let identifyFilter = emptyFilter;
    if (mapFilter) {
        const { identifyLayer, filter, inverseFilter } = mapFilter;
        identifyFilter = filter;
        if (identifyLayer === "county_identify") {
            countyFilter = inverseFilter;
        } else if (identifyLayer === "zip_identify") {
            zipFilter = inverseFilter;
        } else if (identifyLayer === "parcel_identify") {
            parcelFilter = inverseFilter;
        }
    }

    const searchResultGeojson = {
        type: "FeatureCollection",
        features: searchResult?.parcels?.map((parcel) => ({
            id: parcel.id,
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: [parcel.lng, parcel.lat],
            },
        })),
    };

    return (
        <MapContext.Provider value={context}>
            <Map
                ref={mapRef}
                mapboxAccessToken={MAPBOX_TOKEN}
                width="100%"
                height="100%"
                mapStyle="mapbox://styles/kmlandinsights/clyvxfp7m005001px4kv01z02"
                minZoom={4}
                maxZoom={20}
                onClick={onClick}
                interactiveLayerIds={["parcel_identify"]}
                initialViewState={initialViewState}
                hash="view"
            >
                <Source {...parcelSource}>
                    <Layer {...parcelIdentify} />
                    <Layer {...parcelShadow} filter={parcelFilter} />
                    <Layer {...parcelOutline} />
                    <Layer {...parcelOutlineActive} filter={activeParcelFilter} />
                </Source>
                <Source type="geojson" data={searchResultGeojson}>
                    <Layer {...searchResultPoints} />
                </Source>
                <Source {...maptilerSource}>
                    <Layer {...countyIdentify} />
                    <Layer {...countyOutline} filter={identifyFilter} />
                    <Layer {...countyShadow} filter={countyFilter} />
                    <Layer {...zipIdentify} />
                    <Layer {...zipOutline} filter={identifyFilter} />
                    <Layer {...zipShadow} filter={zipFilter} />
                </Source>
                <LogoControl onClick={resetViewState} />
                <SearchControl open={showSearchBox} setOpen={setShowSearchBox} />
                <FilterControl open={showFilterPanel} setOpen={setShowFilterPanel} />
                <DataControl />
                <LayerControl />
                <ToolsControl />
                <ListsControl />
                <HelpControl />
                <NavigationControl position="top-right" />
                <InspectControl position="bottom-right" />
                <FullscreenControl position="bottom-right" />
                <DrawControl position={"bottom-right"} />
                {showSearchBox && (
                    <SearchBox
                        setActiveParcelID={setActiveParcelID}
                        setSearchResult={setSearchResult}
                    />
                )}
                {showFilterPanel && !activeParcelID && (
                    <FilterPanel
                        setOpen={setShowFilterPanel}
                        searchResult={searchResult}
                        setSearchResult={setSearchResult}
                    />
                )}
                {activeParcelID && (
                    <ParcelDetail
                        parcelID={activeParcelID}
                        onClose={() => setActiveParcelID()}
                    />
                )}
            </Map>
        </MapContext.Provider>
    );
}

function getBbox(parcel) {
    if (parcel.bbox) {
        return parcel.bbox;
    }
    return [parcel.lng, parcel.lat, parcel.lng, parcel.lat];
}

function calculateBounds(parcels) {
    if (!parcels || parcels.length === 0) {
        return null;
    }

    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;

    for (const parcel of parcels) {
        const [bottom, left, top, right] = getBbox(parcel);

        if (left < minLat) minLat = left;
        if (right > maxLat) maxLat = right;
        if (bottom < minLng) minLng = bottom;
        if (top > maxLng) maxLng = top;
    }

    const bbox = [
        [minLng, minLat], // Bottom-left corner
        [maxLng, maxLat], // Top-right corner
    ];

    return bbox;
}
